/**
 * Stock symbol utilities for format normalization.
 */

/**
 * Check if the input looks like a stock code (vs Chinese name).
 * Returns true for: 600550, SH:600550, 600550.SH, etc.
 * Returns false for: 保变电气, 中国平安, etc.
 */
/**
 * Broad-based index symbols (sina-prefix convention, lowercase).
 * Mirrors backend `app/core/symbol.py::SymbolNormalizer.INDEX_SYMBOLS` \u2014
 * MUST stay in sync. Indices share the 6-digit number space with stocks
 * (sh000001 = \u4e0a\u8bc1\u6307\u6570 vs 000001.SZ = \u5e73\u5b89\u94f6\u884c), so they need an
 * explicit allowlist instead of pattern-based exchange inference.
 */
const INDEX_SYMBOLS: ReadonlySet<string> = new Set([
  'sh000001', // \u4e0a\u8bc1\u6307\u6570
  'sz399001', // \u6df1\u8bc1\u6210\u6307
  'sh000300', // \u6caa\u6df1300
  'sz399006', // \u521b\u4e1a\u677f\u6307
]);

export function isIndexSymbol(input: string): boolean {
  if (!input) return false;
  return INDEX_SYMBOLS.has(input.trim().toLowerCase());
}

export function isStockCodeFormat(input: string): boolean {
  if (!input) return false;
  const trimmed = input.trim();

  // Contains Chinese characters - likely a name
  if (/[\u4e00-\u9fa5]/.test(trimmed)) {
    return false;
  }

  // Index symbols are a recognized code format \u2014 no further resolution needed.
  if (isIndexSymbol(trimmed)) {
    return true;
  }

  // Check various code patterns
  const patterns = [
    /^\d{6}$/,                    // 600550
    /^\d{6}\.(SZ|SH|BJ)$/i,       // 600550.SH
    /^(SZ|SH|BJ)[:\-]?\d{6}$/i,   // SH:600550, SH600550
    /^\d{6}[:\-](SZ|SH|BJ)$/i,    // 600550:SH
  ];

  return patterns.some(p => p.test(trimmed));
}

/**
 * Normalize stock symbol to standard format (e.g., 002716.SZ).
 * 
 * Supports various input formats:
 * - SZ:002716 -> 002716.SZ
 * - SH:600519 -> 600519.SH
 * - 002716.SZ -> 002716.SZ (already standard)
 * - sz002716 -> 002716.SZ
 * - 002716:SZ -> 002716.SZ
 * - 002716 -> 002716.SZ (defaults based on code pattern)
 */
export function normalizeSymbol(symbol: string): string {
  if (!symbol) return symbol;

  // Indices keep their lowercase sina-prefix form (sh000001 etc.) — they
  // collide with stock codes in the 6-digit space (sh000001 vs 000001.SZ),
  // so passthrough here matches the backend SymbolNormalizer contract and
  // prevents `sh000001` from being mangled into `000001.SH` (a non-existent
  // stock that the backend then resolves to 平安银行 / 000001.SZ).
  const trimmed = symbol.trim();
  if (isIndexSymbol(trimmed)) {
    return trimmed.toLowerCase();
  }

  // Clean up the symbol
  symbol = trimmed.toUpperCase();

  // Pattern: EXCHANGE:CODE (e.g., SZ:002716)
  const exchangeCodeMatch = symbol.match(/^(SZ|SH|BJ)[:\-](\d{6})$/);
  if (exchangeCodeMatch) {
    const [, exchange, code] = exchangeCodeMatch;
    return `${code}.${exchange}`;
  }

  // Pattern: CODE:EXCHANGE (e.g., 002716:SZ)
  const codeExchangeMatch = symbol.match(/^(\d{6})[:\-](SZ|SH|BJ)$/);
  if (codeExchangeMatch) {
    const [, code, exchange] = codeExchangeMatch;
    return `${code}.${exchange}`;
  }

  // Pattern: CODE.EXCHANGE (already standard, e.g., 002716.SZ)
  const standardMatch = symbol.match(/^(\d{6})\.(SZ|SH|BJ)$/);
  if (standardMatch) {
    return symbol; // Already in standard format
  }

  // Pattern: EXCHANGECODE (e.g., SZ002716)
  const prefixMatch = symbol.match(/^(SZ|SH|BJ)(\d{6})$/);
  if (prefixMatch) {
    const [, exchange, code] = prefixMatch;
    return `${code}.${exchange}`;
  }

  // Pattern: Just code (e.g., 002716) - infer exchange
  const codeOnlyMatch = symbol.match(/^(\d{6})$/);
  if (codeOnlyMatch) {
    const code = codeOnlyMatch[1];
    const exchange = inferExchange(code);
    return `${code}.${exchange}`;
  }

  // Return as-is if no pattern matched
  return symbol;
}

/**
 * Infer exchange from stock code.
 */
function inferExchange(code: string): string {
  if (!code || code.length !== 6) return 'SZ';

  const prefix = code.substring(0, 3);
  const firstDigit = code[0];

  // Shenzhen patterns
  if (['000', '001', '002', '003', '300', '301'].includes(prefix)) {
    return 'SZ';
  }

  // Shanghai patterns
  if (['600', '601', '603', '605', '688'].includes(prefix)) {
    return 'SH';
  }

  // Beijing patterns
  if (['4', '8', '9'].includes(firstDigit)) {
    return 'BJ';
  }

  // Default to Shenzhen
  return 'SZ';
}
